import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { pressArticles } from "@/mocks/press";
import { usePageSEO } from '@/hooks/usePageSEO';

const pressSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Jeff Dumè',
    url: 'https://jeffdume.com/press-media',
    image: 'https://jeffdume.com/images/Jeff_D-28.jpg',
    jobTitle: 'Speaker & Workforce Intelligence Advocate',
    description: 'Jeff Dumè featured across publications and industry platforms highlighting workforce strategy, leadership, and entrepreneurship.',
    sameAs: ['https://www.linkedin.com/in/jdume/'],
  },
];

const publicationIcons: Record<string, string> = {
  'CXO Dispatch': 'ri-newspaper-line',
  'Voyage Houston': 'ri-compass-line',
  'CanvasRebel': 'ri-palette-line',
  'Shoutout HTX': 'ri-star-line',
  'Regional Hispanic Contractors Association': 'ri-shield-check-line',
};

const allArticles = [...pressArticles].sort((a, b) => {
  const ya = parseInt(a.year) || 0;
  const yb = parseInt(b.year) || 0;
  return yb - ya;
});

export default function PressMedia() {
  usePageSEO({
    title: 'Jeff Dumé | Press, Media & Interviews | Houston, TX',
    description: 'Jeff Dumé featured in CXO Dispatch, Voyage Houston, CanvasRebel, Shoutout HTX, and industry platforms. Read articles, interviews, and podcast appearances on workforce intelligence, AI governance, and entrepreneurship.',
    keywords: 'Jeff Dumé press, Jeff Dumé media, CXO Dispatch, Voyage Houston, CanvasRebel, workforce intelligence interview, AI governance article, entrepreneurship feature, Houston speaker press',
    canonical: '/press-media',
    ogTitle: 'Jeff Dumé | Press, Media & Interviews',
    ogDescription: 'Featured across publications and platforms. Read interviews and articles on workforce intelligence, AI governance, and entrepreneurship.',
    schema: pressSchema,
    breadcrumbs: [{ name: 'Press & Media', item: '/press-media' }],
  });

  return (
    <div className="bg-charcoal min-h-screen">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-20 sm:pt-24 md:pt-28 pb-12 md:pb-20">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 sm:gap-10 lg:gap-16 items-start">
              <div className="lg:w-[60%] w-full">
                <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3 sm:mb-4">
                  Press &amp; Media
                </p>
                <h1 className="text-white text-2xl sm:text-3xl md:text-4xl lg:text-display font-bold leading-tight mb-4 sm:mb-6 font-display">
                  Featured Across Publications &amp; Industry Platforms
                </h1>
                <p className="text-white/50 text-sm md:text-base leading-relaxed max-w-xl">
                  Articles, interviews, and features covering workforce intelligence, AI governance, structured hiring, and organizational leadership. From CXO Dispatch, Voyage Houston, CanvasRebel, and industry platforms.
                </p>
              </div>

              <div className="lg:w-[40%] w-full max-w-xs sm:max-w-sm lg:max-w-none mx-auto lg:mx-0">
                <div className="relative">
                  <div className="w-full aspect-[4/3] rounded-xl overflow-hidden border border-white/10 bg-dark-card">
                    <img
                      src="/images/Jeff_D-28.jpg"
                      alt="Jeff Dumè featured in press and media publications"
                      loading="eager"
                      fetchPriority="high"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="absolute -bottom-3 -left-3 w-full h-full rounded-xl border border-gold/20 -z-10"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Articles Grid */}
        <section className="py-12 md:py-20 lg:py-24 border-t border-white/5">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-4xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3">
                Articles &amp; Features
              </p>
              <h2 className="text-white text-2xl md:text-3xl font-bold leading-tight mb-3">
                Press, Articles &amp; Media
              </h2>
              <p className="text-white/40 text-sm md:text-base leading-relaxed max-w-lg mx-auto">
                A growing collection of published conversations, interviews, and industry spotlights.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {allArticles.filter(a =>
                ['CXO Dispatch', 'Voyage Houston', 'CanvasRebel', 'Shoutout HTX', 'Regional Hispanic Contractors Association'].includes(a.publication)
              ).map((article) => {
                const icon = publicationIcons[article.publication] || 'ri-article-line';
                return (
                  <div
                    key={article.id}
                    className="group relative bg-dark-card rounded-xl p-5 md:p-6 border border-white/5 hover:border-gold/20 transition-all duration-500"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-gold/10 group-hover:bg-gold/20 transition-colors mt-0.5">
                        <i className={`${icon} text-gold text-lg`}></i>
                      </div>
                      <div className="flex-1 w-0 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="text-gold text-[10px] font-semibold tracking-wide uppercase break-words">
                            {article.publication}
                          </span>
                          {article.year && (
                            <span className="text-white/30 text-[10px] font-medium bg-white/5 px-1.5 py-0.5 rounded">
                              {article.year}
                            </span>
                          )}
                        </div>
                        <h3 className="text-white font-semibold text-sm md:text-base mb-2 group-hover:text-gold-light transition-colors leading-snug break-words">
                          {article.title}
                        </h3>
                        <p className="text-white/40 text-xs md:text-sm leading-relaxed break-words">
                          {article.description}
                        </p>
                        {article.url && (
                          <a
                            href={article.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 inline-flex items-center gap-2 text-gold text-xs font-medium hover:text-gold-light transition-colors"
                          >
                            <i className="ri-article-line"></i>
                            <span>Read Article</span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Timeline — Interviews & Podcasts */}
        <section className="py-12 md:py-20 lg:py-24 border-t border-white/5">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-4xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3">
                Conversations
              </p>
              <h2 className="text-white text-2xl md:text-3xl font-bold leading-tight mb-3">
                Interviews &amp; Podcast Appearances
              </h2>
              <p className="text-white/40 text-sm md:text-base leading-relaxed max-w-lg mx-auto">
                Conversations on workforce intelligence, leadership, entrepreneurship, and the future of human and AI collaboration.
              </p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-8 top-0 bottom-0 w-px bg-gold/30"></div>
              <div className="space-y-10">
                {/* Voyage Houston 2026 */}
                <div className="relative pl-12 md:pl-20">
                  <div className="absolute left-2 md:left-6 top-1 w-5 h-5 rounded-full bg-gold border-4 border-charcoal flex items-center justify-center">
                    <i className="ri-mic-2-line text-charcoal text-[8px]"></i>
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-gold font-bold text-sm">2026</span>
                    <span className="bg-gold/10 text-gold text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide">
                      Interview
                    </span>
                  </div>
                  <div className="bg-dark-card rounded-xl p-5 md:p-6 border border-white/5 hover:border-gold/20 transition-colors">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-lg bg-gold/10">
                        <i className="ri-compass-line text-gold text-lg"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-gold text-[10px] font-semibold tracking-wide uppercase block mb-1">
                          Voyage Houston
                        </span>
                        <h3 className="text-white font-semibold text-base md:text-lg mb-2">
                          Exploring Life &amp; Business with Jeff Dum&eacute; of Mainframe Solutions
                        </h3>
                        <p className="text-white/50 text-sm leading-relaxed">
                          Covered the journey behind Mainframe Solutions, early lessons in entrepreneurship, and how relationships and operational discipline shaped the business.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CXO Dispatch 2026 */}
                <div className="relative pl-12 md:pl-20">
                  <div className="absolute left-2 md:left-6 top-1 w-5 h-5 rounded-full bg-gold border-4 border-charcoal flex items-center justify-center">
                    <i className="ri-mic-2-line text-charcoal text-[8px]"></i>
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-gold font-bold text-sm">2026</span>
                    <span className="bg-gold/10 text-gold text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide">
                      Article
                    </span>
                  </div>
                  <div className="bg-dark-card rounded-xl p-5 md:p-6 border border-white/5 hover:border-gold/20 transition-colors">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-lg bg-gold/10">
                        <i className="ri-newspaper-line text-gold text-lg"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-gold text-[10px] font-semibold tracking-wide uppercase block mb-1">
                          CXO Dispatch
                        </span>
                        <h3 className="text-white font-semibold text-base md:text-lg mb-2">
                          Jeff Dum&eacute;: Reducing Hiring Risk Through Structured Evaluation &amp; Responsible AI Use
                        </h3>
                        <p className="text-white/50 text-sm leading-relaxed">
                          Discussed the role of structured evaluation and responsible AI in improving hiring outcomes while preserving human judgment.
                        </p>
                        <a
                          href="https://www.cxodispatch.com/strategy/jeff-dume-reducing-hiring-risk-through-structured-evaluation-responsible-ai-use/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 inline-flex items-center gap-2 text-gold text-xs font-medium hover:text-gold-light transition-colors"
                        >
                          <i className="ri-article-line"></i>
                          <span>Read Article</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CanvasRebel 2023 */}
                <div className="relative pl-12 md:pl-20">
                  <div className="absolute left-2 md:left-6 top-1 w-5 h-5 rounded-full bg-gold border-4 border-charcoal flex items-center justify-center">
                    <i className="ri-mic-2-line text-charcoal text-[8px]"></i>
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-gold font-bold text-sm">2023</span>
                    <span className="bg-gold/10 text-gold text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide">
                      Interview
                    </span>
                  </div>
                  <div className="bg-dark-card rounded-xl p-5 md:p-6 border border-white/5 hover:border-gold/20 transition-colors">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-lg bg-gold/10">
                        <i className="ri-palette-line text-gold text-lg"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-gold text-[10px] font-semibold tracking-wide uppercase block mb-1">
                          CanvasRebel
                        </span>
                        <h3 className="text-white font-semibold text-base md:text-lg mb-2">
                          Building a One-Stop Operational Support Business
                        </h3>
                        <p className="text-white/50 text-sm leading-relaxed">
                          Shared lessons on entrepreneurship, operational discipline, and building organizations designed for long-term growth.
                        </p>
                        <a
                          href="https://canvasrebel.com/meet-jeff-dume/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 inline-flex items-center gap-2 text-gold text-xs font-medium hover:text-gold-light transition-colors"
                        >
                          <i className="ri-article-line"></i>
                          <span>Read Interview</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Book & Podcast */}
        <section className="py-12 md:py-20 lg:py-24 border-t border-white/5">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-4xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3">
                More Media
              </p>
              <h2 className="text-white text-2xl md:text-3xl font-bold leading-tight mb-3">
                Book &amp; Podcast
              </h2>
              <p className="text-white/40 text-sm md:text-base leading-relaxed max-w-lg mx-auto">
                Additional media appearances covering workforce infrastructure, executive search, and the future of organizational growth.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {allArticles.filter(a => a.type === 'book' || a.type === 'podcast').map((article) => (
                <div
                  key={article.id}
                  className="group relative bg-dark-card rounded-xl p-5 md:p-6 border border-white/5 hover:border-gold/20 transition-all duration-500"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-gold/10 group-hover:bg-gold/20 transition-colors mt-0.5">
                      <i className={`${article.type === 'book' ? 'ri-book-open-line' : 'ri-headphone-line'} text-gold text-lg`}></i>
                    </div>
                    <div className="flex-1 w-0 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-gold text-[10px] font-semibold tracking-wide uppercase break-words">
                          {article.publication}
                        </span>
                        <span className="text-white/30 text-[10px] font-medium bg-white/5 px-1.5 py-0.5 rounded">
                          {article.type === 'book' ? 'Book' : 'Podcast'}
                        </span>
                      </div>
                      <h3 className="text-white font-semibold text-sm md:text-base mb-2 group-hover:text-gold-light transition-colors leading-snug break-words">
                        {article.title}
                      </h3>
                      <p className="text-white/40 text-xs md:text-sm leading-relaxed break-words">
                        {article.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 border-t border-white/5">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto text-center">
            <h2 className="text-white text-xl md:text-2xl font-bold mb-4">
              Media Inquiries
            </h2>
            <p className="text-white/40 text-sm md:text-base mb-8 max-w-lg mx-auto leading-relaxed">
              For press inquiries, interview requests, or media appearances, reach out directly to discuss availability and topics.
            </p>
            <a
              href="mailto:jeff.dume@mainframesolutions.net"
              className="inline-flex items-center gap-2 bg-gold text-charcoal font-semibold text-sm px-8 py-4 rounded-md hover:bg-gold-light transition-colors whitespace-nowrap"
            >
              Contact for Media
              <i className="ri-send-plane-line"></i>
            </a>
          </div>
        </section>

        <section className="py-14 md:py-20">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="w-8 h-[1px] bg-white/10"></span>
              <div className="w-2 h-2 rounded-full bg-gold/40"></div>
              <span className="w-8 h-[1px] bg-white/10"></span>
            </div>
            <p className="text-white/20 text-xs tracking-wide uppercase">
              Conversations that move industries forward
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}